import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from "rxjs";
import {finalize, tap} from "rxjs/operators";
import {CheckoutSteps, DiscountType, OrderChannel, OrderMethod, OrderStatus, PaymentType, TableType} from "./constants";
import {
  BackendService,
  BenefitSelectionJSON,
  CartEntryJSON,
  CartJSON,
  DeliveryAddressJSON,
  OrderResponseJSON,
  PaymentOptionJSON,
} from "./backend.service";
import {Discount, DiscountBenefit} from "./models/discount";
import {Host} from "./models/host";
import {Constraint, Menu, MenuGroup, MenuItem} from "./models/menu";
import {Table} from "./models/table";
import {getCurrentDate} from "./util";

const isEqualSet = (a: Array<any>, b: Array<any>) => {
  const unionSize = new Set([...a, ...b]).size;
  return unionSize === a.length && unionSize === b.length
};

export class CartEntry {
  cart: Cart;
  count: number = 1;
  item: MenuItem;
  title: string = "";       // only relevant for custom item (host-order)
  price: number = null;     // only relevant for custom item (host-order)
  vat_group: number | null = null;   // only relevant for custom item (host-order)
  variant_id: number = null;
  option_ids: Array<number> = [];
  choices: {[id: number]: string} = {}; // key = itemid-variantid or itemid-0 if there's no variant
  choices_txt: string = "";             // list of choices to show in cart
  choices_price: number = 0;            // price of choices in cart
  comment: string = "";

  constructor(cart: Cart, init?: Partial<CartEntry>) {
    this.cart = cart;
    Object.assign(this, init);
  }

  isEqual(other: Partial<CartEntry>): boolean {
    if (this.item==null || other.item==null || this.item.hasChoices() || other.item.hasChoices()) {
      return false;
    }
    return (this.item.id == other.item.id &&
      this.variant_id == other.variant_id &&
      isEqualSet(this.option_ids, (other.option_ids || [])) &&
      this.comment == (other.comment || ""))
  }

  getNameWithVariant(): string {
    if (this.item) {
      let name = this.item.name;
      if (this.variant_id) {
        name += ' (' + this.item.getVariant(this.variant_id).name + ')'
      }
      return name;
    } else {
      return this.title;
    }
  }

  getOptions(): string {
    const names = [];
    this.option_ids.forEach(id => {
      names.push(this.item.getOption(id).name)
    })
    return names.join(", ");
  }

  getPrice(): number {
    if (this.item) {
      let price = parseFloat((this.variant_id) ? this.item.getVariant(this.variant_id).price : this.item.price);
      this.option_ids.forEach(id => {
        price += parseFloat(this.item.getOption(id).price);
      });
      price += this.choices_price;
      return price * this.count;
    } else {
      return this.price;
    }
  }

  setChoices(selectedChoices: {[id: number]: string}) {
    // recalculate price and text
    this.choices = selectedChoices;
    const choices_txts = new Array<string>();
    this.choices_price = 0;
    this.item?.getChoices().forEach((c) => {
      const selectedVal = selectedChoices[c.id],
            selectedChoice = c.choices.find(x => x.key == selectedVal);
      if (selectedChoice) {
        this.choices_price += parseFloat(selectedChoice.price);
        choices_txts.push(c.name + ': ' + selectedChoice.title);
      }
    });
    this.choices_txt = choices_txts.join(', ');
  }

  getChoices() {
    const choice_list = [];
    this.item?.getChoices().forEach((c) => {
      const selectedVal = this.choices[c.id],
            selectedChoice = c.choices.find(x => x.key == selectedVal);
      if (selectedChoice) {
        choice_list.push({name: c.name, selected: selectedChoice.title});
      }
    });
    return choice_list;
  }

  getChoicesText(): string {
    return this.choices_txt;
  }

  hasOpenChoices(): boolean {
    if (!this.item) return false;
    for (let c of this.item.getChoices()) {
      if (!this.choices[c.id]) {
        return true;
      }
    }
    return false;
  }

  /**
   * check constraints for this item and return the blocking
   * constraint (if any applies) or null if everything is fine
   * @param deliveryDate
   */
  checkConstraint(deliveryDate: Date): Constraint {
    if (this.item?.constraint && !this.item.constraint.check(
            [this.cart.method],
            this.cart.table.number,
      [[deliveryDate, deliveryDate]])) {
      return this.item.constraint;
    }
    if (this.item?.group.constraint && !this.item.group.constraint.check(
      [this.cart.method],
            this.cart.table.number,
      [[deliveryDate, deliveryDate]])) {
      return this.item.group.constraint;
    }
    return null;
  }

  getData(): CartEntryJSON {
    return {
      count: this.count,
      item_id: (this.item) ? this.item.id : null,
      variant_id: this.variant_id,
      option_ids: this.option_ids,
      title: this.title,
      price: this.price,
      choices: this.choices,
      choices_txt: this.choices_txt,
      choices_price: this.choices_price,
      comment: this.comment,
      vat_group: this.vat_group
    }
  }
}

export class DiscountEntry {
  cart: Cart;
  discount: Discount;
  amount: number;
  index: number;
  waived: boolean = false;

  constructor(cart: Cart, discount: Discount, amount: number, index: number, waived: boolean = false) {
    this.cart = cart;
    this.discount = discount;
    this.amount = (waived)? 0 : amount;
    this.index = index;
    this.waived = waived;
  }

  getAmount(): number {
    return (this.waived) ? 0 : this.amount;
  }

  needsSelection(): boolean {
    return (!this.waived && this.discount.needsSelection());
  }

  getSelection(benefit_id: number): BenefitSelection {
    return this.cart.benefitSelections.get(`${benefit_id}-${this.index}`);
  }

  hasSelections(): boolean {
    return this.discount.benefits.every((b) => {
      return (b.needsSelection()) ? this.getSelection(b.id).isComplete() : true});
  }

}

export class BenefitSelection {
  id: string;
  benefit: DiscountBenefit;
  title: string;
  text: string;
  group: MenuGroup;
  item: MenuItem;
  variant: number;

  constructor(init?: Partial<BenefitSelection>) {
    Object.assign(this, init);
  }

  isComplete(): boolean {
    return (this.item != null && this.variant != null)
  }

  getName(): string {
    if (this.isComplete()) {
      if (this.item.hasVariants()) {
        return this.item.name + ' (' + this.item.getVariant(this.variant).name + ')'
      } else {
        return this.item.name;
      }
    }
  }

  getData(): BenefitSelectionJSON {
    return {
      benefit_id: this.id,
      item_id: (this.item) ? this.item.id : null,
      variant_id: this.variant
    }
  }
}

export interface MinAmountCheckResult {
  valid: boolean,
  min: number,
  missing: number,
  fee: number
}

export interface DeliveryDay {
  day: number,          // midnight in epoch seconds
  label: string,        // label
  times: Array<number>  // possible preorder times in seconds after midnight
}

export class Cart {
  positions: Array<CartEntry> = []
  address: DeliveryAddressJSON = null;
  payment: string = null;
  method: OrderMethod = null;
  desired_time: number = null;
  discounts: Array<DiscountEntry> = new Array<DiscountEntry>();
  vouchers: Array<DiscountEntry> = new Array<DiscountEntry>();
  discountCodes: Map<string, Discount> = new Map<string, Discount>();
  benefitSelections: Map<string, BenefitSelection> = new Map<string, BenefitSelection>();
  paymentOptions: PaymentOptionJSON[];
  waivedDiscounts: Array<string> = new Array<string>();   // discounts which should not be applied
  tipGiven: number = 0;
  isValidated: boolean = false; // whether current cart (e.g. method, payment, ...) is validated by the server

  constructor(
    public host: Host,
    public menu: Menu,
    public table: Table,
    public service: CartService
  ) {
  }

  getNetTotal(limit_to_group?: number): number {
    let positions = this.positions;
    if (limit_to_group) {
      positions = positions.filter((position) => position.item?.isInGroup(limit_to_group));
    }
    return positions.reduce((acc, entry) => (acc + entry.getPrice()), 0)
  }

  getDiscountTotal(): number {
    return this.discounts.reduce((acc, discount) => (acc + discount.getAmount()), 0)
  }

  getVoucherTotal(): number {
    return this.vouchers.reduce((acc, discount) => (acc + discount.getAmount()), 0)
  }

  getTotal(withVouchers: boolean=true): number {
    const total = this.getNetTotal() - this.getDiscountTotal() + this.getDeliveryFee();
    if (withVouchers) {
      return total - Math.min(total, this.getVoucherTotal());
    } else {
      return total;
    }
  }

  getCount(): number {
    return this.positions.reduce((acc, entry) => (acc + entry.count), 0)
  }

  async load(menu: Menu, data: CartJSON, with_app_only_discounts = true) {
    try {
      this.address = data.address;
      this.payment = data.payment;
      this.method = data.method;
      this.desired_time = data.desired_time;
      this.positions = [];
      data.positions.forEach((pos) => {
        const entry = new CartEntry(this, pos);
        if (pos.item_id) {
          if (menu.items[pos.item_id]) {
            entry.item = menu.items[pos.item_id];
            this.positions.push(entry)
          }
        } else {
          if (pos.title && pos.price) {
            entry.title = pos.title;
            entry.price = pos.price;
            this.positions.push(entry)
          }
        }
      });
      // load discount codes, if any
      if (data.codes) {
        await Promise.all(data.codes.map((code) => this.addDiscountCode(code)));
      }
      // load waived discounts
      if (data.waivedDiscounts) {
        this.waivedDiscounts = data.waivedDiscounts;
      } else {
        this.waivedDiscounts = [];
      }
      this.applyDiscounts(with_app_only_discounts);
      // load selections
      if (data.selections) {
        data.selections.forEach((sel) => {
          if (menu.items.hasOwnProperty(sel.item_id)) {
            const item = menu.items[sel.item_id];
            this.setBenefitSelection(sel.benefit_id, item, sel.variant_id, false);
          }
        });
      }
    } catch (e) {
      // cart has load error, just scrap it
      this.clear();
    }
  }
  clear() {
    this.service.clearCartInStorage();
    this.positions = [];
    this.method = null;
    this.desired_time = null;
    this.payment = null;
    this.paymentOptions = null;
    this.discountCodes.clear();
    this.isValidated = false;
    this.waivedDiscounts = [];
    this.tipGiven = 0;
  }

  save() {
    this.service.saveCartToStorage(this);
  }

  getMinAmount() {
    return this.host.getMinMaxAmount(this.address.city).min
  }

  getDeliveryFee() {
    return this.checkMinOrderAmount().fee;
  }

  /**
   * Get a list of possible delivery days and times with respect to the
   * current city and method.
   *
   * @param now current epoch time in seconds (defaults to system time if not given)
   */
  getPreorderDays(now: number = null): Array<DeliveryDay> {
    return this.host.getPreorderTimes(this.method, this.address.city);
  }

  /**
   * Calculate a possible delivery time, with respect to the cart's method and city.
   *
   * @param desired desired time (if not given, the set desired time of the cart will be used)
   * @returns day, day_label and time are only set if the host has preorder times. dt is always
   *               set and defaults to the earliest delivery time, if no preorder times are given
   *               or desired time is asap (=0)
   */
  getDeliveryTime(desired: number = null): {day: number, day_label: string, time: number, dt: Date} {
    desired = desired || this.desired_time;
    const times = this.getPreorderDays(),
          entry = times.find(d => d.day <= desired && d.day + 24*60*60 > desired);
    let time = null;
    if (entry) {
      time = entry.times.find(t => t == (desired - entry.day))
      if (time == undefined) {
        // if exact time isn't found, choose next possible slot
        time = time = entry.times.find(t => t >= (desired - entry.day))
      }
    }
    if (time != null) {
      const dt = getCurrentDate();
      dt.setTime((entry.day+time)*1000);
      return {
        day: entry.day,
        day_label: entry.label,
        time: time,
        dt: dt
      }
    } else {
      return {
        day: null,
        day_label: null,
        time: null,
        dt: this.getEarliestDeliveryTime()
      }
    }
  }

  /**
   * return the earliest possible delivery time, with respect to the cart's
   * method and city.
   */
  getEarliestDeliveryTime(): Date {
    return this.host.getEarliestDate(this.method, this.address.city);
  }

  /**
   * fix desired time (if less than possible delivery time)
   *
   * Call after changing the city or delivery method/table
   */
  fixDesiredTime() {
    if (this.desired_time > 0) {
      const nextPossible = this.getEarliestDeliveryTime().getTime()/1000;
      if (this.desired_time < nextPossible) {
        this.desired_time = nextPossible;
      }
    }
  }

  setAddress(address: DeliveryAddressJSON) {
    Object.assign(this.address, address);
    this.fixDesiredTime();
    this.applyDiscounts(false);
  }

  canPreorder(): boolean {
    return (this.method != OrderMethod.inhouse && this.host.canPreorder());
  }

  checkMinOrderAmount(): MinAmountCheckResult {
    if (this.method == OrderMethod.delivery) {
      const city = this.host.getCity(this.address.city),
            minAmount = this.getMinAmount(),
            total = this.getNetTotal();
      let valid = minAmount <= total;
      let fee = this.host.getDeliveryFee(this.address.city).min;
      // shall the fee be waived?
      if (valid && city && total > parseFloat(city.waive_fee_amount)) {
        // waive fee
        fee = 0;
      }
      return {
        valid: valid,
        min: minAmount,
        missing: minAmount - this.getNetTotal(),
        fee: fee
      }
    } else {
      return {
        valid: true,
        min: 0,
        missing: 0,
        fee: 0
      }
    }
  }

  /**
   * @returns true if all constraints are met (e.g. nothing is blocking the order)
   */
  checkConstraints(): boolean {
    const dt = this.getDeliveryTime().dt;
    return this.positions.every(p => p.checkConstraint(dt)==null);
  }

  /**
   * @returns whether the customer can choose between different payment methods
   */
  canSelectPayment(): boolean {
    return (this.payment != '-') && this.getVisiblePaymentOptions().length > 1;
  }

  /**
   * load available payment options for this cart from backend
   * @returns available payment options
   */
  async getAvailablePaymentOptions() {
    return this.service.getAvailablePaymentOptions(this).pipe(
      tap((options) => {
        this.paymentOptions = options;
      })).toPromise();
  }

  getVisiblePaymentOptions() {
    if (this.method == OrderMethod.inhouse) {
      // we are not showing card as an extra option on inhouse tables, as it doesn't
      // make sense to ask the customer that if payment is offline anyway
      return this.paymentOptions.filter((o) => (o.slug !== 'card'));
    } else {
      return this.paymentOptions;
    }
  }

  /**
   * set the payment method
   * @param slug
   */
  setPayment(slug: string) {
    const selected = this.paymentOptions.find(o=> o.slug == slug);
    if (selected) {
      this.payment = selected.slug;
    } else {
      this.payment = null;
    }
  }
  getPaymentIsValid(): boolean {
    if (this.getTotal() <= 0 || this.payment == '-') {
      return true;
    }
    if (!this.paymentOptions || !this.payment) {
      return false;
    }
    const selected = this.paymentOptions.find(o=>o.slug == this.payment);
    if (selected) {
      return true;
    }
  }

  async validatePayment(force_reload=false) {
    if (this.getPaymentIsValid() && this.paymentOptions != null && !force_reload) {
      return true;
    }
    // load payment options from server
    await this.getAvailablePaymentOptions();
    if (this.paymentOptions.length == 0) {
      // there are no payment options, set to '-'
      this.payment = '-';
    } else if (this.paymentOptions.length == 1) {
      // there is only one option, we must use it (prevents showing the selection)
      this.payment = this.paymentOptions[0].slug;
    } else if (this.method == OrderMethod.inhouse && this.paymentOptions.length == 2 &&
               this.paymentOptions[0].slug == 'cash' && this.paymentOptions[1].slug == 'card') {
        // for inhouse ordering, we don't force a selection, but default to cash
        this.payment = 'cash';
    }
    // check that payment is in the list
    return this.getPaymentIsValid();
  }

  get selectedPaymentText() {
    const selected = this.paymentOptions.find(o=>o.slug == this.payment);
    return (selected) ? selected.selected_text : '';
  }

  getMethodIsValid(): boolean {
    switch (this.method) {
      case OrderMethod.inhouse:
        return this.table.type == TableType.inhouse;
      case OrderMethod.delivery:
        return this.host.has_delivery;
      case OrderMethod.takeout:
        return this.host.has_takeout;
      default:
        return false;
    }
  }

  setMethod(method: OrderMethod) {
    this.method = method;
    this.isValidated = false;
  }

  validateMethod() {
    if (this.table.type == TableType.inhouse) {
      this.method = OrderMethod.inhouse;
    } else {
      if (this.table.type == TableType.delivery && !this.host.has_delivery) {
        this.method = (this.host.has_takeout) ? OrderMethod.takeout : null;
      }
      if (this.table.type == TableType.takeout && !this.host.has_takeout) {
        this.method = (this.host.has_delivery) ? OrderMethod.delivery : null;
      }
    }
  }

  getAddressIsValid(): boolean {
    if (this.method == OrderMethod.delivery) {
      return (Boolean(this.address.name) && Boolean(this.address.address) && Boolean(this.address.city));
    } else if (this.method == OrderMethod.takeout) {
      return Boolean(this.address.name);
    } else {
      return true;
    }
  }

  getChoicesAreValid(): boolean {
    return this.positions.every((p) => !p.hasOpenChoices());
  }

  isEmpty(): boolean {
    return this.positions.length < 1;
  }

  /**
   * Validate the cart against the backend. Right now, this
   * method will only retrieve the payment options from the
   * server, and do the rest on the client, but in the future
   * we might move more stuff there, to avoid duplicating
   * server code on the client.
   *
   * TODO: move more validations/calculations to the server
   *
   */
  async validate(force=false) {
    this.validateMethod();
    await this.validatePayment(force);
    // recalculate discounts
    // TODO: we need app_only flag @cart
    this.applyDiscounts();
    this.isValidated = true;
    return this.getMethodIsValid()
      && this.getAddressIsValid()
      && this.getPaymentIsValid()
      && this.getChoicesAreValid()
      && this.checkMinOrderAmount().valid
      && this.checkConstraints();
  }

  getData(): CartJSON {
    const data = {
      address: this.address,
      positions: this.positions.filter(item => item.count > 0).map(item => item.getData()),
      payment: this.payment,
      method: this.method,
      desired_time: this.desired_time,
      selections: Array.from(this.benefitSelections.values()).map(item => item.getData()),
      codes: Array.from(this.discountCodes.keys()),
      waivedDiscounts: this.waivedDiscounts,
      status: OrderStatus.NEW,
      tipGiven: this.tipGiven,
      fromHost: false
    }
    // don't transmit address, city when it's takeout
    if (data.method == OrderMethod.takeout) {
      data.address.address = null;
      data.address.city = null;
    }
    return data;
  }

  async addDiscountCode(code) {
    const discount = await this.service.checkDiscountCode(code, this.menu);
    if (discount) {
      if (discount.type == DiscountType.Code) {
        // it is not allowed to apply more than one discount code in a single
        // order, so erase all other discountcodes before adding a new one
        this.discountCodes.forEach((discount, code, map) => {
          if (discount.type == DiscountType.Code) {
            map.delete(code);
          }
        })
      }
      this.discountCodes.set(code, discount);
    }
  }

  removeDiscountCode(code) {
    this.discountCodes.delete(code);
  }

  /**
   * Waive a discount. This is only implemented in host-order and allows a discount
   * to be marked as waived. Does not work for discount codes (use removeDiscountCode instead).
   *
   * @param discountId: id of discount to waive
   * @param index: index of discount to waive
   */
  waiveDiscount(discountId: number, index: number) {
    const id = `${discountId}-${index}`;
    if (!this.waivedDiscounts.some((v) => v == id)) {
      this.waivedDiscounts.push(id);
    }
  }

  /**
   * un-waive a given discount
   * @param discountId
   * @param index
   */
  unwaiveDiscount(discountId: number, index: number) {
    const id = `${discountId}-${index}`;
    this.waivedDiscounts = this.waivedDiscounts.filter(v => v != id);
  }

  /**
   * Add a given discount to this cart. Only for internal usage.
   *
   * @param discount        Discount to apply
   * @param entries         DiscountEntry array (to which `discount` will be appended)
   * @param selections      current selections
   * @param currentTotal    current total (before applying `discount`, but eventually with others)
   * @param index           index (for discounts applied more than once)
   * @param merge           whether to merge the discount to existing (if possible)
   * @returns remaining total after applying `discount`
   */
  private addDiscount(discount: Discount, entries: Array<DiscountEntry>,
                      selections: Map<string, BenefitSelection>, currentTotal: number,
                      index: number, merge=false): number {
    const newEntry = new DiscountEntry(this, discount, discount.getAmount(this, currentTotal), index,
      this.waivedDiscounts.indexOf(`${discount.id}-${index}`) >= 0);
    if (discount.needsSelection()) {
      discount.benefits.filter(b => b.needsSelection()).forEach((b) => {
        const selectionId = b.id.toString() + '-' + index.toString()
        const sel = new BenefitSelection(
          {id: selectionId,
            benefit: b,
            group: b.group,
            item: b.item,
            title: discount.title,
            text: discount.text}
        );
        if (this.benefitSelections.has(selectionId)) {
          sel.item = this.benefitSelections.get(selectionId).item;
          sel.variant = this.benefitSelections.get(selectionId).variant;
        }
        selections.set(selectionId, sel);
      });
    }
    if (newEntry.discount.includesFreeItem() || newEntry.amount > 0) {
      if (merge && !discount.needsSelection() && newEntry.amount > 0) {
        const existing = entries.find((e) => (e.discount == newEntry.discount));
        if (existing) {
          existing.amount += newEntry.amount;
        } else {
          entries.push(newEntry);
        }
      } else {
        entries.push(newEntry);
      }
    }
    return currentTotal - newEntry.amount;
  }

  /**
   * (Re-)applies all discounts, discount codes and vouchers.
   * This method is idempotent, e.g. calling it several times
   * will not duplicate discounts.
   *
   * `withAppOnly` decides whether to apply app_only discounts, too.
   * `applyDefaultSelections` will select a default item/variant for any benefit without selection
   *
   */
  applyDiscounts(withAppOnly: boolean = true, applyDefaultSelections: boolean = false) {
    const newEntries = new Array<DiscountEntry>(),
      newVouchers = new Array<DiscountEntry>(),
      newSelections = new Map<string, BenefitSelection>();
    let remainingTotal = this.getNetTotal();

    // apply generic discounts
    this.host.discounts.forEach((discount) => {
      let times = discount.checkIfApplies(this);
      if (discount.app_only && !withAppOnly) {
        return;
      }
      if (times) {
        for (let i=0; i< times; i++) {
          remainingTotal = this.addDiscount(discount, newEntries, newSelections, remainingTotal, i, true);
        }
      }
    });

    // apply codes
    Array.from(this.discountCodes.values()).forEach((discount) => {
      if (discount.type == DiscountType.Voucher) {
        this.addDiscount(discount, newVouchers, newSelections, remainingTotal, 0);
      } else {
        if (discount.checkIfApplies(this)) {
          this.addDiscount(discount, newEntries, newSelections, remainingTotal, 0);
        }
      }
    })

    this.discounts = newEntries;
    this.vouchers = newVouchers;
    this.benefitSelections = newSelections;
  }

  hasOpenSelection(): boolean {
    return this.nextOpenSelection() != null;
  }

  nextOpenSelection(): BenefitSelection {
    for (let b of this.benefitSelections.entries()) {
      const index = b[0].split('-')[1],
            discountid = b[1].benefit.discount.id,
            waiveId = `${discountid}-${index}`;

      if (!b[1].isComplete() && this.waivedDiscounts.indexOf(waiveId) < 0) {
        return b[1];
      }
    }
    return null;
  }

  setBenefitSelection(id: string, item: MenuItem, variant: number, commit: boolean = true) {
    const sel = this.benefitSelections.get(id);
    if (sel) {
      sel.item = item;
      sel.variant = variant;
    }
    if (commit) {
      this.save();
      return this.nextOpenSelection();
    } else {
      return null;
    }
  }

  /**
   * Return the next step which the user must complete.
   *
   * We simply check which data we already have and return the next step
   * which is needed.
   */
  getNextCheckoutStep() {
    // -----------------------------------------------------------------------
    // Check order method. Selection is necessary if the table is not inhouse
    // and the host offers both delivery and takeout.
    // -----------------------------------------------------------------------
    if (!this.method) {
      if (this.table.type == TableType.inhouse) {
        // fix method and continue
        this.method = OrderMethod.inhouse;
      } else {
        if (this.host.has_delivery && this.host.has_takeout) {
          // selection is necessary
          return CheckoutSteps.chooseMethod;
        } else if (this.host.has_delivery) {
          // fix method and continue
          this.method = OrderMethod.delivery;
        } else if (this.host.has_takeout) {
          // fix method and continue
          this.method = OrderMethod.takeout;
        }
      }
    } else if (this.method == OrderMethod.delivery) {
      // address and city must be set
      if (!this.address.city || !this.address.address) {
        return CheckoutSteps.chooseMethod;
      }
    }
    // -----------------------------------------------------------------------
    // Check contact data. At least the name must be set, whether email and
    // phone are required is a host setting. None of the above applies if we
    // have an inhouse order.
    // -----------------------------------------------------------------------
    if (this.method != OrderMethod.inhouse || this.host.contact_data_inhouse) {
      if (!this.address.name) {
        return CheckoutSteps.enterContact;
      }
      if (!this.address.phone && this.host.require_delivery_phone) {
        return CheckoutSteps.enterContact;
      }
      if (!this.address.email && this.host.require_delivery_email) {
        return CheckoutSteps.enterContact;
      }
    }
    // -----------------------------------------------------------------------
    // Check payment method, if the host offers them
    // -----------------------------------------------------------------------
    if (!this.getPaymentIsValid()) {
        return CheckoutSteps.choosePayment;
    }
    // -----------------------------------------------------------------------
    // Check if the shop is open, or will open soon. If not, show the time
    // selection dialog, so it is obvious to the customer that the order will
    // not be coming soon. Obviously this doesn't apply for inhouse orders or
    // when desired_time is already set
    // -----------------------------------------------------------------------
    if (this.method != OrderMethod.inhouse) {
      if (this.desired_time===null) {
        const oh = this.host.getOpeningHours();
        if (!oh.isOpen && !oh.willOpenSoon) {
          return CheckoutSteps.chooseTime;
        }
      }
    }
    // -----------------------------------------------------------------------
    // Check whether we have unselected benefits, if so, show selection
    // -----------------------------------------------------------------------
    // TODO: problematic if new objects are created all the time?!
    this.applyDiscounts();
    if (this.hasOpenSelection()) {
      return CheckoutSteps.selectBenefit;
    }
    // -----------------------------------------------------------------------
    // everything is fine, show the review screen
    // -----------------------------------------------------------------------
    return CheckoutSteps.review;
  }
}

/**
 * Represents a sent/open order. Later, we can use this to update the status,
 * either by polling or through websocket.
 */
export class Order {
  host: Host;
  id: number;
  number: string;
  data: CartJSON;
  total: string;
  created: string;
  token: string;
  status: string;
  method: OrderMethod;
  payment: string;
  payment_type: PaymentType;
  estimated_time: string;

  constructor(init?: Partial<Order>) {
    Object.assign(this, init);
  }
}

@Injectable({providedIn: 'root'})
export class CartService {

  cart: Cart = null;
  orders: Record<number, Order> = {};
  isSending: boolean = false;
  isDemo: boolean = false;
  storageKey: string = 'cart';

  constructor(private http: HttpClient,
              private backendService: BackendService) {
  }

  async getCartFromStorage(menu: Menu, host: Host, table: Table, with_app_only_discounts=true) {
    if (this.cart == null) {
      this.cart = new Cart(host, menu, table, this);
      let result = window.localStorage.getItem(this.storageKey);
      if (result) {
        await this.cart.load(menu, JSON.parse(result), with_app_only_discounts);
      }
      if (this.cart.address == null) {
        this.cart.address = this.getAddressFromStorage();
      }
      // validate city exists, and is in a valid zone
      if (this.cart.address.city) {
        try {
          if (!host.getCity(this.cart.address.city)) {
            this.cart.address.city = null;
          }
        } catch (e) {
          this.cart.address.city = null;
        }
      }
      // validate (and possibly fix) method and payment
      await this.cart.validate();
    }
    return this.cart;
  }

  async checkDiscountCode(code: string, menu: Menu): Promise<Discount> {
    return this.backendService.checkCode(code).toPromise().then((data) => {
      return new Discount(data, menu);
    }).catch(() => {
        return null;
      }
    )
  }

  getAvailablePaymentOptions(cart: Cart) {
    return this.backendService.getPaymentOptions(cart.getData());
  }

  saveCartToStorage(cart) {
    window.localStorage.setItem(this.storageKey, JSON.stringify(cart.getData()))
  }

  clearCartInStorage() {
    window.localStorage.removeItem(this.storageKey);
  }

  addToCart(entry: Partial<CartEntry>, forceNew: boolean = false): CartEntry {
    let pos = this.cart.positions.find(item => item.isEqual(entry))
    if (pos && this.cart.host.merge_positions && !forceNew) {
      pos.count += entry.count;
    } else {
      pos = new CartEntry(this.cart, entry);
      // trigger recalc of choice price, text
      if (entry.choices) {
        pos.setChoices(entry.choices);
      }
      this.cart.positions.push(pos)
    }
    this.cart.isValidated = false;
    this.saveCartToStorage(this.cart);
    return pos;
  }

  sendOrder(cart: Cart, table: Table=null, status=OrderStatus.NEW, channel = OrderChannel.GuestOrder): Observable<OrderResponseJSON> {
    const orderData = cart.getData();
    orderData.status = status;
    orderData.channel = channel;
    if (this.isSending) {
      // prevent duplicate submission
      return of(null);
    } else {
      this.isSending = true;
    }
    return this.backendService.sendOrder(orderData, table).pipe(
      tap<OrderResponseJSON>((data) => {
        this.orders[data.orderId] = new Order({
          id: data.orderId,
          number: data.orderNumber,
          token: data.updateToken,
          data: orderData,
          total: data.orderTotal,
          method: data.orderMethod,
          payment: cart.payment,
          payment_type: (data.redirect) ? PaymentType.ONLINE : PaymentType.OFFLINE,
          estimated_time: data.estimatedTime,
          created: new Date().toJSON()
        });
        this.saveOrdersToStorage(this.orders);
        // keep address for the next order
        this.saveAddressToStorage(orderData.address);
        // clear cart
        this.cart.clear();
        this.cart.address = this.getAddressFromStorage();
      }),
      finalize(() => {this.isSending = false;})
    )
  }

  sendServiceCall(table: Table) {
    if (this.isSending) {
      // prevent duplicate submission
      return of(null);
    } else {
      this.isSending = true;
    }
    return this.backendService.sendServiceCall(table).pipe(
      tap(() => {}),
      finalize(() => {this.isSending = false;})
    )
  }

  getOrder(id: number): Order {
    this.orders = this.getOrdersFromStorage();
    return this.orders[id];
  }

  updateOrder(id: number, status: string, payment: string) {
    const order = this.getOrder(id);
    if (order) {
      order.status = status;
      this.updateOrderInStorage(order);
      return this.backendService.updateOrder({id, token: order.token, status, payment})
    }
  }

  getOrdersFromStorage(): Record<number, Order> {
    try {
      let result = window.localStorage.getItem('orderList');
      if (result) {
        const data = JSON.parse(result);
        const retval = {};
        Object.keys(data).forEach((k) => {
          retval[k] = new Order(data[k]);
        })
        return retval;
      } else {
        return {};
      }
    } catch (e) {
      // ignore, we can't do anything about it
      return {};
    }
  }

  saveOrdersToStorage(orders: Record<number, Order>) {
    try {
      window.localStorage.setItem('orderList', JSON.stringify(orders))
    } catch (e) {
      // ignore
    }
  }

  updateOrderInStorage(order: Order) {
    try {
      const orders = this.getOrdersFromStorage();
      orders[order.id] = order;
      this.saveOrdersToStorage(orders);
    } catch (e) {
      // ignore
    }
  }

  getAddressFromStorage(): DeliveryAddressJSON {
    const dflt = {name: '', address: '', city: null, phone: '', email: '', notes: ''};
    try {
      let result = window.localStorage.getItem('deliveryAddress');
      if (result) {
        Object.assign(dflt, JSON.parse(result));
      }
    } catch (e) {
      // ignore, it's just a convenience function
    }
    return dflt;
  }

  saveAddressToStorage({name = '', address = '', city = null, phone = '', email = ''}) {
    try {
      const data = this.getAddressFromStorage();
      if (name) {
        data.name = name;
      }
      if (phone) {
        data.phone = phone;
      }
      if (email) {
        data.email = email;
      }
      if (address) {
        data.address = address;
      }
      if (city && data.city != city) {
        data.city = city;
        data.address = address;
      }
      window.localStorage.setItem('deliveryAddress', JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  }

  async hasVatGroups() {
    const data = await this.backendService.getT4DData();
    return
  }


}
